"use client"

import * as React from 'react'
import { useRouter, useParams } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { updateCrud, deleteCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'

const VEHICLE_TYPE_OPTIONS = [
  { value: 'electric', label: 'Electric' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'dual_mode', label: 'Dual Mode' },
  { value: 'shunter', label: 'Shunter' },
]

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'in_use', label: 'In Use' },
]

type LocomotiveData = {
  id: string
  evn: string
  name: string
  series: string
  vehicle_type: string
  status: string
  weight_tonnes: number
  max_speed_kmh: number | null
  power_kw: number | null
  ecm_name: string
  ecm_certificate_id: string | null
  ecm_valid_until: string | null
  notes: string | null
}

type RouteParams = {
  id?: string | string[]
  slug?: string[]
}

function readRouteId(params?: RouteParams | null): string {
  if (!params) return ''
  if (typeof params.id === 'string') return params.id
  if (Array.isArray(params.id)) return params.id[0] ?? ''
  if (Array.isArray(params.slug)) return params.slug.at(-1) ?? ''
  return ''
}

export default function EditLocomotivePage({ params }: { params?: RouteParams }) {
  const t = useT()
  const router = useRouter()
  const routeParams = useParams()
  const id = readRouteId(params) || readRouteId(routeParams as RouteParams | undefined)
  const scopeVersion = useOrganizationScopeVersion()

  const [locomotive, setLocomotive] = React.useState<LocomotiveData | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [loadError, setLoadError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!id) {
      setLocomotive(null)
      setLoadError(t('rolling_stock.locomotives.detail.error.load', 'Locomotive not found'))
      setIsLoading(false)
      return
    }
    let cancelled = false

    async function load() {
      setIsLoading(true)
      setLoadError(null)
      try {
        const fallback = { items: [] as LocomotiveData[] }
        const res = await apiCall<{ items: LocomotiveData[]; error?: string }>(
          `/api/rolling-stock/locomotives?ids=${encodeURIComponent(id)}`,
          undefined,
          { fallback },
        )
        if (cancelled) return

        if (!res.ok) {
          setLocomotive(null)
          setLoadError(
            typeof res.result?.error === 'string'
              ? res.result.error
              : t('rolling_stock.locomotives.detail.error.load', 'Failed to load locomotive'),
          )
          return
        }

        const item = Array.isArray(res.result?.items) ? res.result.items[0] : null
        if (item) {
          setLocomotive(item)
          return
        }

        setLocomotive(null)
        setLoadError(t('rolling_stock.locomotives.detail.error.load', 'Locomotive not found'))
      } catch (error) {
        if (cancelled) return
        setLocomotive(null)
        setLoadError(
          error instanceof Error && error.message
            ? error.message
            : t('rolling_stock.locomotives.detail.error.load', 'Failed to load locomotive'),
        )
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    load()
    return () => { cancelled = true }
  }, [id, scopeVersion, t])

  const fields = React.useMemo<CrudField[]>(() => [
    { id: 'evn', label: t('rolling_stock.locomotives.fields.evn', 'EVN (12 digits)'), type: 'text', required: true, maxLength: 12 },
    { id: 'name', label: t('rolling_stock.locomotives.fields.name', 'Name'), type: 'text', required: true },
    { id: 'series', label: t('rolling_stock.locomotives.fields.series', 'Series'), type: 'text', required: true },
    { id: 'vehicleType', label: t('rolling_stock.locomotives.fields.vehicleType', 'Vehicle Type'), type: 'select', required: true, options: VEHICLE_TYPE_OPTIONS },
    { id: 'status', label: t('rolling_stock.locomotives.fields.status', 'Status'), type: 'select', required: true, options: STATUS_OPTIONS },
    { id: 'weightTonnes', label: t('rolling_stock.locomotives.fields.weightTonnes', 'Weight (tonnes)'), type: 'number', required: true, min: 0 },
    { id: 'maxSpeedKmh', label: t('rolling_stock.locomotives.fields.maxSpeedKmh', 'Max Speed (km/h)'), type: 'number', min: 0 },
    { id: 'powerKw', label: t('rolling_stock.locomotives.fields.powerKw', 'Power (kW)'), type: 'number', min: 0 },
    { id: 'ecmName', label: t('rolling_stock.locomotives.fields.ecmName', 'ECM Name'), type: 'text', required: true },
    { id: 'ecmCertificateId', label: t('rolling_stock.locomotives.fields.ecmCertificateId', 'ECM Certificate ID'), type: 'text' },
    { id: 'ecmValidUntil', label: t('rolling_stock.locomotives.fields.ecmValidUntil', 'ECM Valid Until'), type: 'date' },
    { id: 'notes', label: t('rolling_stock.locomotives.fields.notes', 'Notes'), type: 'textarea' },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'identification',
      title: t('rolling_stock.locomotives.groups.identification', 'Identification'),
      fields: ['evn', 'name', 'series', 'vehicleType', 'status'],
    },
    {
      id: 'technical',
      title: t('rolling_stock.locomotives.groups.technical', 'Technical Specs'),
      fields: ['weightTonnes', 'maxSpeedKmh', 'powerKw'],
    },
    {
      id: 'ecm',
      title: t('rolling_stock.locomotives.groups.ecm', 'Entity in Charge of Maintenance'),
      fields: ['ecmName', 'ecmCertificateId', 'ecmValidUntil'],
    },
    {
      id: 'other',
      title: t('rolling_stock.locomotives.groups.other', 'Other'),
      fields: ['notes'],
    },
  ], [t])

  if (isLoading) return <LoadingMessage label="" />
  if (loadError || !locomotive) return <ErrorMessage label={loadError ?? 'Not found'} />

  const initialValues = {
    evn: locomotive.evn,
    name: locomotive.name,
    series: locomotive.series,
    vehicleType: locomotive.vehicle_type,
    status: locomotive.status,
    weightTonnes: locomotive.weight_tonnes,
    maxSpeedKmh: locomotive.max_speed_kmh ?? undefined,
    powerKw: locomotive.power_kw ?? undefined,
    ecmName: locomotive.ecm_name,
    ecmCertificateId: locomotive.ecm_certificate_id ?? undefined,
    ecmValidUntil: locomotive.ecm_valid_until ?? undefined,
    notes: locomotive.notes ?? undefined,
  }

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={t('rolling_stock.locomotives.edit.title', 'Edit Locomotive')}
          backHref="/backend/rolling-stock/locomotives"
          fields={fields}
          groups={groups}
          initialValues={initialValues}
          submitLabel={t('rolling_stock.locomotives.edit.submit', 'Save Changes')}
          cancelHref="/backend/rolling-stock/locomotives"
          onDelete={async () => {
            await deleteCrud('rolling-stock/locomotives', { id: locomotive.id })
            flash(t('rolling_stock.locomotives.flash.deleted', 'Locomotive deleted'), 'success')
            router.push('/backend/rolling-stock/locomotives')
          }}
          deleteRedirect="/backend/rolling-stock/locomotives"
          onSubmit={async (values) => {
            await updateCrud('rolling-stock/locomotives', { id: locomotive.id, ...values })
            flash(t('rolling_stock.locomotives.flash.updated', 'Locomotive updated'), 'success')
            router.push('/backend/rolling-stock/locomotives')
          }}
        />
      </PageBody>
    </Page>
  )
}
